import React from 'react'
import { Link } from 'react-router-dom'
import { Download, Minus, Plus } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'
import CurrencyLogo from '@/components/CurrencyLogo'
import type { LiquidityPositionsControllerResult } from '@/features/liquidity/useLiquidityPositionsController'
import { useAppWeb3 } from '@/hooks'
import type { Pair } from '@/sdk'
import { currencyId } from '@/utils/currencyId'
import { unwrappedToken } from '@/utils/wrappedCurrency'
import type { ResolveWidgetRoute } from '@/widgets/navigation'
import { MobileSwapScreenHeader } from '@/widgets/swap/nutrimatic/mobile/SwapNutrimaticMobileSimpleViews'
import { LiquidityLpTokenLegend } from '@/widgets/liquidity/positions/LiquidityLpTokenLegend'

import { LiquidityNutrimaticMobilePlaceholderScene } from './LiquidityNutrimaticPlaceholderScene'

function NutrimaticPositionsList({
  pairs,
  selectedPair,
  onSelect,
}: {
  pairs: Pair[]
  selectedPair: Pair | null
  onSelect: (pair: Pair) => void
}) {
  return (
    <ul className="nm-mobile-simple__positions-list">
      {pairs.map((pair) => {
        const c0 = unwrappedToken(pair.token0)
        const c1 = unwrappedToken(pair.token1)
        const selected = selectedPair?.liquidityToken.address === pair.liquidityToken.address
        return (
          <li key={pair.liquidityToken.address}>
            <button
              type="button"
              onClick={() => onSelect(pair)}
              className={cn(
                'nm-mobile-simple__positions-list-item',
                selected && 'nm-mobile-simple__positions-list-item--active',
              )}
            >
              <div className="flex -space-x-2">
                <CurrencyLogo currency={c0} size="24px" />
                <CurrencyLogo currency={c1} size="24px" />
              </div>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {c0.symbol}/{c1.symbol}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** Panel derecho del hub en estado posiciones: inventario + acciones. */
export function LiquidityNutrimaticPositionsInstrument({
  controller,
  resolveRoute,
  selectedPair,
  onSelectPair,
}: {
  controller: LiquidityPositionsControllerResult
  resolveRoute: ResolveWidgetRoute
  selectedPair: Pair | null
  onSelectPair: (pair: Pair) => void
}) {
  const { chainId } = useAppWeb3()
  const { account, addLiquidityPath, allV2PairsWithLiquidity, importPoolPath, v2IsLoading } = controller

  const pairAddPath =
    selectedPair && chainId != null
      ? resolveRoute({
          type: 'add',
          token0: currencyId(unwrappedToken(selectedPair.token0), chainId),
          token1: currencyId(unwrappedToken(selectedPair.token1), chainId),
        })
      : addLiquidityPath

  const pairRemovePath =
    selectedPair && chainId != null
      ? resolveRoute({
          type: 'remove',
          token0: currencyId(unwrappedToken(selectedPair.token0), chainId),
          token1: currencyId(unwrappedToken(selectedPair.token1), chainId),
        })
      : '#'

  return (
    <div className="nm-mobile-simple__convert-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.liquidityHub.positionsTitle}
        hideBack
        hideTrailing
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--scroll flex min-h-0 flex-1 flex-col" data-mobile-scroll-container>
        <p className="nm-mobile-simple__leg-label px-1">{nutrimaticLabels.liquidityHub.performKicker}</p>

        <div className="mb-3 flex flex-wrap gap-2">
          <Link to={addLiquidityPath} className="nm-mobile-simple__positions-chip nm-mobile-simple__positions-chip--primary">
            <Plus className="size-3.5" aria-hidden />
            {nutrimaticLabels.liquidityHub.actionAdd}
          </Link>
          <Link to={importPoolPath} className="nm-mobile-simple__positions-chip">
            <Download className="size-3.5" aria-hidden />
            {nutrimaticLabels.liquidityHub.actionImport}
          </Link>
        </div>

        {!account ? (
          <LiquidityNutrimaticMobilePlaceholderScene id="connect-wallet" />
        ) : v2IsLoading ? (
          <LiquidityNutrimaticMobilePlaceholderScene id="loading" />
        ) : allV2PairsWithLiquidity.length === 0 ? (
          <LiquidityNutrimaticMobilePlaceholderScene id="no-positions" />
        ) : (
          <>
            <LiquidityLpTokenLegend className="mb-3 px-1" variant="desktop" />
            <NutrimaticPositionsList
              pairs={allV2PairsWithLiquidity}
              selectedPair={selectedPair}
              onSelect={onSelectPair}
            />
          </>
        )}
      </div>

      {selectedPair && account && allV2PairsWithLiquidity.length > 0 ? (
        <div className="nm-mobile-simple__input-dock nm-mobile-simple__input-dock--desktop">
          <div className="nm-mobile-simple__footer nm-mobile-simple__footer--stacked">
            <Link to={pairRemovePath} className="nm-mobile-simple__cta nm-mobile-simple__cta--secondary inline-flex items-center justify-center gap-2">
              <Minus className="size-4" aria-hidden />
              {nutrimaticLabels.liquidityHub.actionRemovePair}
            </Link>
            <Link to={pairAddPath} className="nm-mobile-simple__cta inline-flex items-center justify-center gap-2">
              <Plus className="size-4" aria-hidden />
              {nutrimaticLabels.liquidityHub.actionAddPair}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
